import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { titleKeyFromMessageKey } from '@/api';
import { ErrorState } from '@/components/feedback/ErrorState';
import { PageMeta } from '@/components/layout/PageMeta';
import { Stack } from '@/components/layout/Stack';
import { SupportContacts } from '@/components/layout/SupportContacts';
import { env } from '@/config/env';
import { formatMoney } from '@/helpers';
import { resolveSession, useSessionDispatch, useSessionState } from '@/state';

export function SessionErrorPage() {
  const { t, i18n } = useTranslation();
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const { t: tCashier } = useTranslation('cashier');
  const { t: tOutcome } = useTranslation('outcome');
  const { t: tMethods } = useTranslation('methods');
  const state = useSessionState();
  const dispatch = useSessionDispatch();
  const abortRef = useRef<AbortController | null>(null);
  const submitting = useRef(false);
  const error = state.error;
  const retryable = error?.retryable === true && state.token !== null;
  const chooseAnother =
    error?.status === 422 ||
    error?.code === 'BUSINESS_RULE' ||
    error?.code === 'CONNECTOR_UNSUPPORTED';

  let title: string;
  let body: string;
  if (error === null) {
    title = tCashier('unknownStatus.title');
    body = tCashier('unknownStatus.body');
  } else {
    title = t(titleKeyFromMessageKey(error.messageKey));
    const currency = state.deposit?.currency;
    const amountLabel =
      error.limitAmount === undefined
        ? undefined
        : currency !== undefined && currency !== ''
          ? formatMoney(error.limitAmount, currency, i18n.language, state.deposit?.decimalPlaces)
          : error.limitAmount;
    body =
      amountLabel !== undefined
        ? t(error.messageKey, { amount: amountLabel })
        : t(error.messageKey);
  }

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const handleRetry = () => {
    if (chooseAnother) {
      dispatch({ type: 'ERROR_CLEARED' });
      dispatch({ type: 'CLIENT_INTENT_SET', intent: { step: 'none' } });
      return;
    }
    const token = state.token;
    if (!retryable || token === null || submitting.current) return;
    submitting.current = true;
    dispatch({ type: 'ERROR_CLEARED' });
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    void resolveSession(dispatch, token, controller.signal).finally(() => {
      submitting.current = false;
    });
  };

  const retryLabel = chooseAnother
    ? tMethods('unavailable.action')
    : retryable
      ? tCommon('actions.tryAgain')
      : undefined;

  return (
    <Stack gap="lg">
      <PageMeta title={title} />
      <ErrorState
        title={title}
        body={body}
        onRetry={chooseAnother || retryable ? handleRetry : undefined}
        retryLabel={retryLabel}
        retryLoadingLabel={tCommon('labels.loading')}
        isRetrying={state.phase === 'resolving'}
        publicId={state.deposit?.publicId}
        publicIdLabel={tErrors('reference.publicId')}
        requestId={error?.requestId}
        requestIdLabel={tErrors('reference.requestId')}
        copyLabel={tCommon('actions.copy')}
        copiedLabel={tCommon('actions.copied')}
        manualCopyLabel={tCommon('actions.manualCopy')}
      />
      <SupportContacts
        email={state.branding?.supportEmail ?? null}
        phone={state.branding?.supportPhone ?? null}
        fallbackUrl={env.supportUrl}
        heading={tOutcome('support.title')}
        emailLabel={tCommon('footer.supportEmail')}
        phoneLabel={tCommon('footer.supportPhone')}
        fallbackLabel={tCommon('footer.support')}
      />
    </Stack>
  );
}
